import {
  invitationInspectResponseSchema,
  loginResponseSchema,
  meResponseSchema,
  mfaSetupResponseSchema,
  mfaStatusSchema,
  recoveryCodesResponseSchema,
  storeSessionsResponseSchema,
  type LoginRequest,
  type MeResponse,
  type StoreCapability,
} from '@avrahami/contracts';
import { fetchSession, type SessionState } from '@avrahami/ui';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { can } from '../lib/access';
import { api } from '../lib/api';

export type { SessionState };

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: () => fetchSession(api),
  staleTime: 30_000,
  retry: false,
});

/** The signed-in member (undefined while loading or signed out). */
export function useMe(): MeResponse | undefined {
  const session = useQuery(sessionQuery);
  return session.data?.kind === 'signed-in' ? session.data.me : undefined;
}

/** Whether the member's role in the active store has a capability. */
export function useCan(capability: StoreCapability): boolean {
  return can(useMe(), capability);
}

export const sessionApi = {
  login: (body: LoginRequest) => api.request('POST', '/v1/auth/login', loginResponseSchema, body),
  verifyMfa: (code: string) =>
    api.request('POST', '/v1/auth/mfa/verify', meResponseSchema, { code }),
  logout: () => api.send('POST', '/v1/auth/logout'),
  /** Platform staff: the entry token of a store becomes this admin's session (adr/0031). */
  enterStore: (token: string) =>
    api.request('POST', '/v1/auth/store-entry', meResponseSchema, { token }),
  /** Another store of the member, in a session of its own (adr/0033). */
  openStore: (tenantId: string) =>
    api.request('POST', '/v1/auth/store-session', meResponseSchema, { tenantId }),
  /** The stores this browser is signed in to here, for a tab that has no store yet. */
  storeSessions: () => api.request('GET', '/v1/auth/store-sessions', storeSessionsResponseSchema),
  setupMfa: () => api.request('POST', '/v1/auth/mfa/setup', mfaSetupResponseSchema),
  enableMfa: (code: string) =>
    api.request('POST', '/v1/auth/mfa/enable', recoveryCodesResponseSchema, { code }),
  disableMfa: (code: string) => api.send('POST', '/v1/auth/mfa/disable', { code }),
  regenerateRecoveryCodes: (code: string) =>
    api.request('POST', '/v1/auth/mfa/recovery-codes', recoveryCodesResponseSchema, { code }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.send('POST', '/v1/auth/change-password', body),
};

export const mfaStatusQuery = queryOptions({
  queryKey: ['mfa-status'],
  queryFn: () => api.request('GET', '/v1/auth/mfa', mfaStatusSchema),
});

/** The public invitation page. */
export const invitationApi = {
  inspect: (token: string) =>
    api.request('POST', '/v1/auth/invitation/inspect', invitationInspectResponseSchema, { token }),
  accept: (token: string, password: string) =>
    api.send('POST', '/v1/auth/invitation/accept', { token, password }),
};

/** "Forgot password" (adr/0035): the link is mailed, then works like an invitation link. */
export const passwordResetApi = {
  request: (email: string) => api.send('POST', '/v1/auth/password-reset', { email }),
  inspect: (token: string) =>
    api.request('POST', '/v1/auth/password-reset/inspect', invitationInspectResponseSchema, {
      token,
    }),
  complete: (token: string, password: string) =>
    api.send('POST', '/v1/auth/password-reset/complete', { token, password }),
};
